
import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
    user: User | null;
    session: Session | null;
    loading: boolean;
    isSyncing: boolean;
    signOut: () => Promise<void>;
    subscriptionStatus: string | null;
    refreshSubscription: () => Promise<void>;
    userRole: 'admin' | 'user' | null;
    setUserRole: (role: 'admin' | 'user' | null) => void;
    onboardingCompleted: boolean;
    confirmOnboardingAction: () => void;
    atomicReset: () => Promise<void>;
    hardDeleteAccount: () => Promise<void>;
    sessionInitialized: boolean;
    maintenanceMode: boolean;
    marcAvatarUrl: string;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    session: null,
    loading: true,
    isSyncing: false,
    signOut: async () => { },
    subscriptionStatus: null,
    refreshSubscription: async () => { },
    userRole: null,
    setUserRole: () => { },
    onboardingCompleted: false,
    confirmOnboardingAction: () => { },
    atomicReset: async () => { },
    hardDeleteAccount: async () => { },
    sessionInitialized: false,
    maintenanceMode: false,
    marcAvatarUrl: '/assets/images/landing/marc.jpg',
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
    const [marcAvatarUrl, setMarcAvatarUrl] = useState<string>('/assets/images/landing/marc.jpg');
    const [userRole, setUserRole] = useState<'admin' | 'user' | null>(() => {
        return localStorage.getItem('aura_user_role') as 'admin' | 'user' | null;
    });
    const [onboardingCompleted, setOnboardingCompleted] = useState(() => {
        return localStorage.getItem('aura_onboarding_completed') === 'true';
    });
    const [sessionInitialized, setSessionInitialized] = useState(false);
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const isSyncingRef = React.useRef(false);
    const isResettingRef = React.useRef(false);
    const realtimeChannelRef = React.useRef<any>(null);

    // Fetch Maintenance Mode & Global Settings
    const fetchMaintenanceMode = async () => {
        try {
            const { data, error } = await supabase
                .from('settings')
                .select('maintenance_mode, marc_avatar_url')
                .limit(1)
                .single();
            if (!error && data) {
                setMaintenanceMode(data.maintenance_mode);
                if (data.marc_avatar_url) {
                    setMarcAvatarUrl(data.marc_avatar_url);
                }
            }
        } catch (e) {
            console.error("[AuthContext] Failed to fetch settings", e);
        }
    };

    // HELPER: Sync cache to state BEFORE unlocking UI
    const hydrateStates = (userId: string) => {
        try {
            // V3000: PWA GARBAGE COLLECTOR
            // If the user left the app in the background while processing and comes back 4+ hours later,
            // iOS Safari might restore the old localStorage, causing infinite ghost skeletons.
            // We purge these flags before hydration if they are stale.
            const processStartStr = localStorage.getItem('aura_diet_process_start');
            if (processStartStr) {
                const processStart = parseInt(processStartStr, 10);
                if (Date.now() - processStart > 4 * 60 * 60 * 1000) {
                    console.log("[GarbageCollector] Purging stale loading flags from PWA cache.");
                    localStorage.removeItem('aura_diet_process_start');
                    localStorage.removeItem('aura_target_completion_time');
                    localStorage.removeItem('aura_diet_status');
                    localStorage.removeItem('aura_generation_type');
                }
            }

            const lastId = localStorage.getItem('aura_last_user_id');
            if (lastId !== userId) {
                if (lastId) {
                    console.log("[AuthContext] User changed! Clearing previous user's cached data...");
                    const keysToClear = [
                        'aura_user_data',
                        'aura_subscription_status',
                        'aura_onboarding_completed',
                        'aura_user_role',
                        'aura_active_program',
                        'aura_history',
                        'aura_weekly_checkins',
                        'aura_premium_welcome_seen',
                        'aura_tutorial_completed',
                        'aura_workout_tutorial_completed',
                        'aura_diet_status',
                        'aura_target_completion_time',
                        'aura_diet_process_start',
                        'aura_generation_type',
                        'aura_pending_completion_reveal',
                        'aura_pending_welcome_sequence'
                    ];
                    keysToClear.forEach(k => localStorage.removeItem(k));
                    Object.keys(localStorage).forEach(key => {
                        if (key.startsWith('aura_habits_') || key.startsWith('aura_current_diet_') || key.startsWith('plan_v11_cleanest_')) {
                            localStorage.removeItem(key);
                        }
                    });
                }
                return { hasCache: false, onboardingCompleted: false };
            }

            const userData = localStorage.getItem('aura_user_data');
            
            // Critical Fix: Only assume we have cache if we actually have user data.
            // Returning hasCache: true on empty data unlocks the loading screen before DB sync!
            if (!userData) {
                return { hasCache: false, onboardingCompleted: false };
            }

            const savedSub = localStorage.getItem('aura_subscription_status');
            const onboardingFlag = localStorage.getItem('aura_onboarding_completed') === 'true';
            
            const parsed = JSON.parse(userData);
            const cachedOnboarding = onboardingFlag || parsed.onboardingCompleted === true || parsed.onboarding_completed === true;
            setOnboardingCompleted(cachedOnboarding);

            if (savedSub) {
                setSubscriptionStatus(savedSub);
            }
            const savedRole = localStorage.getItem('aura_user_role');
            if (savedRole) {
                setUserRole(savedRole as 'admin' | 'user');
            }

            // V556: If the cache says they AREN'T onboarded, we don't trust it. 
            // It might be stale (e.g. they onboarded on another device).
            // We force hasCache = false so the UI remains locked behind the loading spinner 
            // until syncUserData validates the truth from Supabase profiles.
            if (!cachedOnboarding) {
                return { hasCache: false, onboardingCompleted: false };
            }

            return { hasCache: true, onboardingCompleted: cachedOnboarding };
        } catch (e) {
            return { hasCache: false, onboardingCompleted: false };
        }
    };

    const syncUserData = async (userId: string) => {
        if (isSyncingRef.current) return;
        if (isResettingRef.current) {
            console.log("[AuthContext] Sync blocked due to active reset.");
            return;
        }

        isSyncingRef.current = true;
        setIsSyncing(true);
        try {
            let essential = null;
            let essError = null;

            // V633: Remove all artificial delays and retry loops.
            // If the Postgres trigger is slow and we get 0 rows, we immediately fall through to the healing logic
            // which safely upserts the profile. This completely eliminates the 4-second UI freeze for new users.
            const res = await supabase
                .from('profiles')
                .select('id, subscription_status, onboarding_completed, role')
                .eq('id', userId)
                .single();
            
            essential = res.data;
            essError = res.error;

            if (essError) {
                console.warn("[AuthContext] Essential sync error:", essError);
                // If profile is missing (PGRST116: 0 rows returned), attempt to heal the profile immediately instead of waiting.
                if (essError.code === 'PGRST116' || essError.message?.includes('0 rows')) {
                    console.error("[AuthContext] USER PROFILE NOT FOUND after retries. Attempting to heal profile...");
                    try {
                        const fallbackProfile = {
                            id: userId,
                            subscription_status: 'free',
                            onboarding_completed: false,
                            role: 'user',
                            stats: {}
                        };
                        const { error: healError } = await supabase.from('profiles').upsert(fallbackProfile);
                        if (healError) {
                            console.error("[AuthContext] Profile healing failed:", healError);
                        } else {
                            console.log("[AuthContext] Profile healed successfully.");
                        }
                        essential = fallbackProfile;
                        essError = null;
                    } catch (e) {
                        console.error("[AuthContext] Exception during profile healing:", e);
                    }
                } else {
                    // It's a network error or something else.
                    // If we have no cache, we CANNOT let them proceed because their app will crash or they'll be sent to the questionnaire.
                    // We must retain the loading state or show an error.
                    // For now, if we have cache, we just let them use offline mode.
                    const localFlag = localStorage.getItem('aura_onboarding_completed') === 'true';
                    if (localFlag) {
                         setOnboardingCompleted(true);
                    }
                }
            }
            
            if (essential) {
                const subStatus = essential.subscription_status || 'free';
                setSubscriptionStatus(subStatus);
                localStorage.setItem('aura_subscription_status', subStatus);

                const role = essential.role || 'user';
                setUserRole(role);
                localStorage.setItem('aura_user_role', role);

                const localFlag = localStorage.getItem('aura_onboarding_completed') === 'true';
                const isDone = essential.onboarding_completed || localFlag;
                setOnboardingCompleted(isDone);

                if (isDone && !localFlag) {
                    localStorage.setItem('aura_onboarding_completed', 'true');
                }
            }
            setLoading(false);

            // Await the IIFE to prevent race condition redirects
            await (async () => {
                try {
                    const today = new Date().toISOString().split('T')[0];
                    const [profileRes, logsRes, checkinsRes, habitsRes] = await Promise.all([
                        supabase.from('profiles').select('stats, nutrition_plan, aura_diet_status, aura_diet_process_start, aura_target_completion_time').eq('id', userId).single(),
                        supabase.from('workout_logs').select('*').eq('user_id', userId).order('date', { ascending: false }),
                        supabase.from('weekly_checkins').select('*').eq('user_id', userId).order('date', { ascending: false }),
                        supabase.from('user_habits').select('habits').eq('user_id', userId).eq('date', today).single()
                    ]);

                    if (profileRes.data) {
                        let profileData = profileRes.data;
                        let { stats, nutrition_plan } = profileRes.data;
                        if (stats) {
                            if (typeof stats === 'string') {
                                try { stats = JSON.parse(stats); } catch(e) {}
                            }

                            const isProUser = ['active', 'trialing', 'pro'].includes(essential?.subscription_status || '');
                            const hasPhotos = stats.photos?.front && stats.photos?.side && stats.photos?.back;
                            const hasPlan = stats.nutritionPlan || stats.diet || nutrition_plan;

                            if (isProUser && !hasPhotos && !hasPlan && profileData.aura_diet_status !== 'pending_photos') {
                                console.log("[AuthSync] Premium user has no photos and no plan. Force setting DB status to pending_photos.");
                                supabase.from('profiles').update({
                                    aura_diet_status: 'pending_photos',
                                    aura_diet_process_start: null,
                                    aura_target_completion_time: null
                                }).eq('id', userId).then(({ error }) => {
                                    if (error) console.error("[AuthSync] Failed to sync pending_photos status to DB:", error);
                                    window.dispatchEvent(new Event('aura_diet_updated'));
                                });
                                profileData.aura_diet_status = 'pending_photos';
                                profileData.aura_diet_process_start = null;
                                profileData.aura_target_completion_time = null;
                            } else if (isProUser && hasPlan && profileData.aura_diet_status === 'pending_photos') {
                                console.log("[AuthSync] Premium user has a plan but status is pending_photos. Healing to completed.");
                                supabase.from('profiles').update({
                                    aura_diet_status: 'completed',
                                    aura_diet_process_start: null,
                                    aura_target_completion_time: null
                                }).eq('id', userId).then(({ error }) => {
                                    if (error) console.error("[AuthSync] Failed to heal status to completed in DB:", error);
                                    window.dispatchEvent(new Event('aura_diet_updated'));
                                });
                                profileData.aura_diet_status = 'completed';
                                profileData.aura_diet_process_start = null;
                                profileData.aura_target_completion_time = null;
                            }
                            // V555: PROTECT LOCAL CONSENT (If server write previously failed or is stale)
                            const currentLocal = JSON.parse(localStorage.getItem('aura_user_data') || '{}');
                            if (currentLocal.consent?.health_data_consent && !stats.consent?.health_data_consent) {
                                console.log("[AuthSync] Preserving local consent bypass over stale server data.");
                                stats.consent = currentLocal.consent;
                            }

                            // V2043: CLEANUP STALE DIET KEYS FROM STATS
                            // Prevent stale diet status inside the 'stats' JSONB from overriding root columns and triggering infinite loops
                            delete stats.aura_diet_status;
                            delete stats.aura_diet_process_start;
                            delete stats.aura_target_completion_time;

                            localStorage.setItem('aura_user_data', JSON.stringify(stats));

                            // Timestamp-based synchronization for custom programs on boot
                            try {
                                const localCustom = localStorage.getItem('aura_custom_programs');
                                let localPrograms: any[] = [];
                                if (localCustom) {
                                    try { localPrograms = JSON.parse(localCustom) || []; } catch(e) {}
                                }
                                
                                const localUpdatedRaw = localStorage.getItem('aura_custom_programs_updated_at');
                                let localUpdated = localUpdatedRaw ? parseInt(localUpdatedRaw, 10) : 0;
                                if (isNaN(localUpdated)) localUpdated = 0;
                                
                                const cloudPrograms = stats.custom_programs || [];
                                const cloudUpdated = stats.custom_programs_updated_at || 0;
                                
                                if (cloudUpdated > localUpdated) {
                                    localStorage.setItem('aura_custom_programs', JSON.stringify(cloudPrograms));
                                    localStorage.setItem('aura_custom_programs_updated_at', String(cloudUpdated));
                                    window.dispatchEvent(new Event('storage'));
                                } else if (localUpdated > cloudUpdated || (cloudUpdated === 0 && localPrograms.length > 0)) {
                                    const newTimestamp = localUpdated > 0 ? localUpdated : Date.now();
                                    if (localUpdated === 0) {
                                        localStorage.setItem('aura_custom_programs_updated_at', String(newTimestamp));
                                    }
                                    stats.custom_programs = localPrograms;
                                    stats.custom_programs_updated_at = newTimestamp;
                                    localStorage.setItem('aura_user_data', JSON.stringify(stats));
                                    
                                    supabase.from('profiles').update({ stats }).eq('id', userId).then(({ error }) => {
                                        if (error) console.error("[AuthSync] Failed to upload newer local custom programs:", error);
                                    });
                                } else if (cloudPrograms.length > 0 && localPrograms.length === 0 && localUpdated === 0) {
                                    localStorage.setItem('aura_custom_programs', JSON.stringify(cloudPrograms));
                                    localStorage.setItem('aura_custom_programs_updated_at', String(cloudUpdated));
                                    window.dispatchEvent(new Event('storage'));
                                }
                            } catch (e) {
                                console.error("[AuthSync] Error syncing custom programs on boot:", e);
                            }
                            
                            // V6010: CRITICAL FIX - ALWAYS SYNC DIET CACHE ON BOOT
                            // If we don't do this, NutritionDashboardScreen remains stuck showing the old `aura_current_diet` forever.
                            const planToCache = stats.nutritionPlan || stats.diet;
                            if (planToCache) {
                                localStorage.setItem(`aura_current_diet_${userId}`, typeof planToCache === 'string' ? planToCache : JSON.stringify(planToCache));
                            }

                            // V6020: Sync active program to localStorage on boot/login
                            if (stats.active_program) {
                                localStorage.setItem('aura_active_program', typeof stats.active_program === 'string' ? stats.active_program : JSON.stringify(stats.active_program));
                                window.dispatchEvent(new CustomEvent('aura_workout_updated'));
                            }
                            
                            // V1100/V2055: CRITICAL - Update primitive keys as fallback for DietGlobalWorker
                            // Intelligent Lag Protector using exact timestamp diffs
                            let isLagProtected = false;
                            const localStatus = localStorage.getItem('aura_diet_status');
                            const localStart = localStorage.getItem('aura_diet_process_start');
                            if (
                                localStatus && 
                                (localStatus === 'generating_initial' || localStatus === 'trigger_update' || localStatus === 'updating' || localStatus === 'processing') &&
                                localStart &&
                                (profileData.aura_diet_status === 'idle' || profileData.aura_diet_status === 'completed' || profileData.aura_diet_status === 'error_generation' || !profileData.aura_diet_status)
                            ) {
                                const cloudStart = profileData.aura_diet_process_start;
                                const diff = cloudStart ? (Number(localStart) - Number(cloudStart)) : 99999999;
                                const timeSinceLocalStart = Date.now() - Number(localStart);
                                
                                if (diff > 300000) {
                                    // V2056: Protect local state for the entire natural duration of a diet generation (35 mins)
                                    const maxLagAllowed = 2100000;
                                    
                                    if (timeSinceLocalStart < maxLagAllowed) {
                                        isLagProtected = true;
                                        console.log(`[AuthSync] Initial fetch lag protector active. DB is STALE (diff: ${diff}ms). Preserving local diet state.`);
                                    }
                                } else {
                                    console.log(`[AuthSync] Initial fetch DB data is FRESH (diff: ${diff}ms). Accepting cloud status.`);
                                    isLagProtected = false;
                                }
                            }

                            if (!isLagProtected) {
                                let finalStatus = profileData.aura_diet_status || 'idle';
                                
                                // V2111: CLOUD EXPIRATION SHIELD IN BOOT
                                const isCloudGenerating = finalStatus === 'trigger_update' || finalStatus === 'updating' || finalStatus === 'generating_initial' || finalStatus === 'processing';
                                if (isCloudGenerating) {
                                    const cStart = profileData.aura_diet_process_start;
                                    if (!cStart || (Date.now() - Number(cStart) > 2100000)) {
                                         console.warn("[AuthSync] DB is stuck on an expired generation. Forcing 'completed' to prevent UI lock.");
                                         finalStatus = 'completed';
                                    }
                                }

                                if (finalStatus !== 'idle') {
                                    localStorage.setItem('aura_diet_status', finalStatus);
                                }
                                
                                const isTimerStillRunning = profileData.aura_target_completion_time && Number(profileData.aura_target_completion_time) > Date.now();
                                const isActivelyGenerating = finalStatus === 'trigger_update' || finalStatus === 'updating' || finalStatus === 'generating_initial' || finalStatus === 'processing' || (finalStatus === 'completed' && isTimerStillRunning);

                                if (profileData.aura_target_completion_time && isActivelyGenerating) {
                                    localStorage.setItem('aura_target_completion_time', String(profileData.aura_target_completion_time));
                                } else if (!isActivelyGenerating) {
                                    localStorage.removeItem('aura_target_completion_time');
                                }
                                
                                if (profileData.aura_diet_process_start && isActivelyGenerating) {
                                    localStorage.setItem('aura_diet_process_start', String(profileData.aura_diet_process_start));
                                } else if (!isActivelyGenerating) {
                                    localStorage.removeItem('aura_diet_process_start');
                                }

                                if (stats.aura_generation_type) {
                                    localStorage.setItem('aura_generation_type', stats.aura_generation_type);
                                }
                            }
                            // Tutorial flags are independent of diet lag — always sync them
                            if (stats.aura_tutorial_completed) {
                                localStorage.setItem('aura_tutorial_completed', 'true');
                            }
                            if (stats.aura_workout_tutorial_completed) {
                                localStorage.setItem('aura_workout_tutorial_completed', 'true');
                            }
                            // Signal that stats (including tutorial flags) are now in localStorage
                            window.dispatchEvent(new CustomEvent('aura_stats_synced'));
                        }
                        if (nutrition_plan) localStorage.setItem(`plan_v11_cleanest_${userId}`, JSON.stringify(nutrition_plan));
                    }
                    if (logsRes.data) {
                        try {
                            localStorage.setItem('aura_history', JSON.stringify(logsRes.data));
                        } catch (e) {
                            console.warn("[AuthSync] Failed to save aura_history to localStorage:", e);
                        }
                    }
                    if (checkinsRes.data) {
                        try {
                            localStorage.setItem('aura_weekly_checkins', JSON.stringify(checkinsRes.data));
                        } catch (e) {
                            console.warn("[AuthSync] Failed to save aura_weekly_checkins to localStorage:", e);
                        }
                    }
                    if (habitsRes.data?.habits) {
                        try {
                            localStorage.setItem(`aura_habits_${today}`, JSON.stringify(habitsRes.data.habits));
                        } catch (e) {
                            console.warn("[AuthSync] Failed to save aura_habits to localStorage:", e);
                        }
                    }
                } catch (e) {
                    console.error("[AuthSync] Background task failed:", e);
                } finally {
                    setIsSyncing(false);
                    isSyncingRef.current = false;
                    window.dispatchEvent(new Event('aura_sync_complete'));
                }
            })();

        } catch (error) {
            console.error('Error in Universal Sync:', error);
            setLoading(false);
        } finally {
            setIsSyncing(false);
            isSyncingRef.current = false;
        }
    };

    const subscribeToProfileChanges = (userId: string) => {
        if (realtimeChannelRef.current) {
            supabase.removeChannel(realtimeChannelRef.current);
        }

        realtimeChannelRef.current = supabase
            .channel(`profile_changes_${userId}`)
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'profiles',
                    filter: `id=eq.${userId}`
                },
                (payload) => {
                    console.log("[AuthContext] Realtime profile update received:", payload.new);
                    const newData = payload.new;

                    if (newData.nutrition_plan) {
                        localStorage.setItem(`plan_v11_cleanest_${userId}`, JSON.stringify(newData.nutrition_plan));
                        window.dispatchEvent(new CustomEvent('aura_diet_updated'));
                    }

                    if (newData.subscription_status) {
                        setSubscriptionStatus(newData.subscription_status);
                        localStorage.setItem('aura_subscription_status', newData.subscription_status);
                        // Force a sync event so that UI updates immediately
                        window.dispatchEvent(new CustomEvent('aura_sync_complete'));
                    }

                    if (newData.stats) {
                        let parsedStats = newData.stats;
                        if (typeof parsedStats === 'string') {
                            try { parsedStats = JSON.parse(parsedStats); } catch(e) {}
                        }

                        const isProUser = ['active', 'trialing', 'pro'].includes(newData.subscription_status || '') || subscriptionStatus === 'pro' || subscriptionStatus === 'active';
                        const hasPhotos = parsedStats.photos?.front && parsedStats.photos?.side && parsedStats.photos?.back;
                        const hasPlan = parsedStats.nutritionPlan || parsedStats.diet || newData.nutrition_plan;

                        if (isProUser && !hasPhotos && !hasPlan && newData.aura_diet_status !== 'pending_photos') {
                            console.log("[AuthSync Realtime] Premium user has no photos and no plan. Force setting DB status to pending_photos.");
                            supabase.from('profiles').update({
                                aura_diet_status: 'pending_photos',
                                aura_diet_process_start: null,
                                aura_target_completion_time: null
                            }).eq('id', userId).then(({ error }) => {
                                if (error) console.error("[AuthSync Realtime] Failed to sync pending_photos status to DB:", error);
                                window.dispatchEvent(new Event('aura_diet_updated'));
                            });
                            newData.aura_diet_status = 'pending_photos';
                            newData.aura_diet_process_start = null;
                            newData.aura_target_completion_time = null;
                        } else if (isProUser && hasPlan && newData.aura_diet_status === 'pending_photos') {
                             console.log("[AuthSync Realtime] Premium user has a plan but status is pending_photos. Healing to completed.");
                             supabase.from('profiles').update({
                                 aura_diet_status: 'completed',
                                 aura_diet_process_start: null,
                                 aura_target_completion_time: null
                             }).eq('id', userId).then(({ error }) => {
                                 if (error) console.error("[AuthSync Realtime] Failed to heal status to completed in DB:", error);
                                 window.dispatchEvent(new Event('aura_diet_updated'));
                             });
                             newData.aura_diet_status = 'completed';
                             newData.aura_diet_process_start = null;
                             newData.aura_target_completion_time = null;
                        }
                        // V555: PROTECT LOCAL CONSENT DURING REALTIME SYNC
                        let currentLocal = {};
                        try {
                            const localRaw = localStorage.getItem('aura_user_data');
                            if (localRaw) {
                                currentLocal = JSON.parse(localRaw);
                                if (typeof currentLocal === 'string') currentLocal = JSON.parse(currentLocal);
                            }
                        } catch(e) {}

                        if ((currentLocal as any).consent?.health_data_consent && !parsedStats.consent?.health_data_consent) {
                            console.log("[AuthSync] Realtime: Preserving local consent over stale server event.");
                            parsedStats.consent = (currentLocal as any).consent;
                        }

                        // V2043: CLEANUP STALE DIET KEYS FROM STATS
                        delete parsedStats.aura_diet_status;
                        delete parsedStats.aura_diet_process_start;
                        delete parsedStats.aura_target_completion_time;

                        localStorage.setItem('aura_user_data', JSON.stringify(parsedStats));

                        // Timestamp-based synchronization for custom programs in real-time
                        try {
                            const localCustom = localStorage.getItem('aura_custom_programs');
                            let localPrograms: any[] = [];
                            if (localCustom) {
                                try { localPrograms = JSON.parse(localCustom) || []; } catch(e) {}
                            }
                            
                            const localUpdatedRaw = localStorage.getItem('aura_custom_programs_updated_at');
                            let localUpdated = localUpdatedRaw ? parseInt(localUpdatedRaw, 10) : 0;
                            if (isNaN(localUpdated)) localUpdated = 0;
                            
                            const cloudPrograms = parsedStats.custom_programs || [];
                            const cloudUpdated = parsedStats.custom_programs_updated_at || 0;
                            
                            if (cloudUpdated > localUpdated) {
                                localStorage.setItem('aura_custom_programs', JSON.stringify(cloudPrograms));
                                localStorage.setItem('aura_custom_programs_updated_at', String(cloudUpdated));
                                window.dispatchEvent(new Event('storage'));
                            }
                        } catch (e) {
                            console.error("[AuthSync] Error syncing custom programs in realtime:", e);
                        }

                        // V6010: CRITICAL FIX - REALTIME DIET CACHE SYNC
                        const rtPlan = parsedStats.nutritionPlan || parsedStats.diet;
                        if (rtPlan) {
                            localStorage.setItem(`aura_current_diet_${userId}`, typeof rtPlan === 'string' ? rtPlan : JSON.stringify(rtPlan));
                        }

                        // V2050/V2055: APLICAR LAG PROTECTOR INTELIGENTE EN REALTIME WEBSOCKETS
                        // Comparamos el timestamp exacto para saber si el update es viejo o la generación final real.
                        let isLagProtected = false;
                        const localStatus = localStorage.getItem('aura_diet_status');
                        const localStart = localStorage.getItem('aura_diet_process_start');
                        if (
                            localStatus && 
                            (localStatus === 'generating_initial' || localStatus === 'trigger_update' || localStatus === 'updating' || localStatus === 'processing') &&
                            localStart &&
                            (newData.aura_diet_status === 'idle' || newData.aura_diet_status === 'completed' || newData.aura_diet_status === 'error_generation' || !newData.aura_diet_status)
                        ) {
                            const cloudStart = newData.aura_diet_process_start;
                            const diff = cloudStart ? (Number(localStart) - Number(cloudStart)) : 99999999;
                            const timeSinceLocalStart = Date.now() - Number(localStart);
                            
                            if (diff > 300000) {
                                // V2056: Protect local state for the entire natural duration of a diet generation (35 mins)
                                const maxLagAllowed = 2100000;
                                
                                if (timeSinceLocalStart < maxLagAllowed) {
                                    isLagProtected = true;
                                    console.log(`[AuthSync] Realtime lag protector active. Payload STALE (diff: ${diff}ms). Preserving local state.`);
                                }
                            } else {
                                console.log(`[AuthSync] Realtime payload is FRESH (diff: ${diff}ms). Accepting cloud status: ${newData.aura_diet_status}`);
                                isLagProtected = false;
                            }
                        }

                        // V1150 FIX: Realtime variables must be extracted individually 
                        // so that BottomNav and DietGlobalWorker's explicitly separate keys
                        // remain synchronized and avoid the read-replica override fallback.
                        // DB EXTRACTION: Realtime payload includes root columns
                        if (!isLagProtected) {
                            let finalStatus = newData.aura_diet_status || localStorage.getItem('aura_diet_status') || 'idle';
                            
                            // V2111: CLOUD EXPIRATION SHIELD IN REALTIME
                            const isCloudGenerating = finalStatus === 'trigger_update' || finalStatus === 'updating' || finalStatus === 'generating_initial' || finalStatus === 'processing';
                            if (isCloudGenerating) {
                                const cStart = newData.aura_diet_process_start;
                                if (!cStart || (Date.now() - Number(cStart) > 2100000)) {
                                     console.warn("[AuthSync Realtime] DB is stuck on an expired generation. Forcing 'completed'.");
                                     finalStatus = 'completed';
                                }
                            }

                            if (finalStatus !== 'idle') {
                                localStorage.setItem('aura_diet_status', finalStatus);
                            }
                            
                            const isTimerStillRunningRealtime = newData.aura_target_completion_time && Number(newData.aura_target_completion_time) > Date.now();
                            const isActivelyGenerating = finalStatus === 'trigger_update' || finalStatus === 'updating' || finalStatus === 'generating_initial' || finalStatus === 'processing' || (finalStatus === 'completed' && isTimerStillRunningRealtime);
                            
                            if (newData.aura_diet_process_start && isActivelyGenerating) {
                                localStorage.setItem('aura_diet_process_start', String(newData.aura_diet_process_start));
                            } else if (!isActivelyGenerating) {
                                localStorage.removeItem('aura_diet_process_start');
                            }
                            
                            if (newData.aura_target_completion_time && isActivelyGenerating) {
                                localStorage.setItem('aura_target_completion_time', String(newData.aura_target_completion_time));
                            } else if (!isActivelyGenerating) {
                                localStorage.removeItem('aura_target_completion_time');
                            }
                        }

                        // Broadcast BOTH events immediately to bind Chat and BottomNav universally
                        window.dispatchEvent(new CustomEvent('aura_stats_updated'));
                        window.dispatchEvent(new CustomEvent('aura_diet_updated'));

                        if (parsedStats.active_program) {
                            localStorage.setItem('aura_active_program', JSON.stringify(parsedStats.active_program));
                            window.dispatchEvent(new CustomEvent('aura_workout_updated'));
                        }
                    }
                }
            )
            .subscribe((status) => {
                console.log(`[AuthContext] Realtime status for ${userId}:`, status);
            });
    };


    // SIMULATION MODE CONSTANTS
    const SIMULATION_MODE = false; // TOGGLE THIS FOR SIMULATION
    const MOCK_USER: any = {
        id: 'mock-user-id-123',
        email: 'simulation@aura.com',
        user_metadata: { full_name: 'Simulated User' },
        aud: 'authenticated',
        role: 'authenticated',
        app_metadata: { provider: 'email' },
        created_at: new Date().toISOString()
    };

    useEffect(() => {
        const bootTime = Date.now();
        const minBootDelay = 1800; // Minimum splash screen time to prevent flash

        const unlockUI = () => {
            const elapsed = Date.now() - bootTime;
            const remaining = Math.max(0, minBootDelay - elapsed);
            setTimeout(() => {
                setLoading(false);
                setSessionInitialized(true);
            }, remaining);
        };

        const safetyTimeout = setTimeout(unlockUI, 8000);

        fetchMaintenanceMode(); // Check maintenance on mount

        // Use getUser() as it forces a server validation, ensuring deleted users are caught instantly on reload
        supabase.auth.getUser().then(({ data: { user }, error }) => {
            if (error) {
                const isNetworkError = error.message?.includes('FetchError') || error.message?.includes('Failed to fetch') || error.status === 0 || error.message?.toLowerCase().includes('network');
                if (!isNetworkError) {
                    console.warn("[AuthContext] Auth error on mount. Server says session may be invalid:", error);
                    // V2056: We do NOT aggressively clear the session here. We let the session refresh mechanism or explicit API calls fail naturally.
                } else {
                    console.warn("[AuthContext] Network error during getUser(), ignoring to preserve local session.", error);
                }
            } else if (!user) {
                console.warn("[AuthContext] Invalid or missing user on mount.");
                // We do NOT aggressively sign out here either. If there's no user, getSession will handle it.
            }
            // We do NOT stop execution here because we still need to set session to null to unblock UI
            supabase.auth.getSession().then(({ data: { session } }) => {
                if (isResettingRef.current) return;
            if (isResettingRef.current) return;

            // SIMULATION OVERRIDE
            if (SIMULATION_MODE && !session) {
                console.log("[AuthContext] 🚨 SIMULATION MODE ACTIVE: Using Mock User 🚨");
                const mockSession: any = {
                    access_token: 'mock-token',
                    token_type: 'bearer',
                    expires_in: 3600,
                    refresh_token: 'mock-refresh',
                    user: MOCK_USER,
                    expires_at: Date.now() + 3600000
                };
                setSession(mockSession);
                setUser(MOCK_USER);
                localStorage.setItem('aura_last_user_id', MOCK_USER.id);

                // MOCK HYDRATION
                // V555: SIMULATION STATE PERSISTENCE
                // We check if we already have a stored state for the simulation to avoid resetting every reload.
                const storedOnboardingStatus = localStorage.getItem('aura_mock_onboarding_completed');

                let isCompleted = false;
                let mockStats: any = {};

                if (storedOnboardingStatus) {
                    isCompleted = storedOnboardingStatus === 'true';
                    const storedProfile = localStorage.getItem('aura_mock_user_profile');
                    if (storedProfile) {
                        mockStats = JSON.parse(storedProfile).stats || {};
                    }
                } else {
                    // FRESH START
                    isCompleted = false;
                    mockStats = { name: 'Simulated User' };
                }

                // Sync to standard Aura keys for components to use

                // V555: CONSENT PRESERVATION
                // Check if we have a locally saved consent in the 'dirty' aura_user_data that should be preserved across reloads
                const existingDirty = JSON.parse(localStorage.getItem('aura_user_data') || '{}');
                if (existingDirty.consent) {
                    mockStats.consent = existingDirty.consent;
                }

                localStorage.setItem('aura_user_data', JSON.stringify(mockStats));
                const storedSubStatus = localStorage.getItem('aura_subscription_status') || 'pro';
                localStorage.setItem('aura_subscription_status', storedSubStatus);
                localStorage.setItem('aura_onboarding_completed', String(isCompleted));

                setSubscriptionStatus(storedSubStatus);
                setOnboardingCompleted(isCompleted);
                unlockUI();
                return;
            }

            setSession(session);
            const activeUser = session?.user ?? null;
            setUser(activeUser);

            if (activeUser) {
                const { hasCache } = hydrateStates(activeUser.id);
                localStorage.setItem('aura_last_user_id', activeUser.id);

                // CRUCIAL: Set initialized AFTER hydration to prevent premature redirects
                if (hasCache) {
                    unlockUI();
                }

                syncUserData(activeUser.id).finally(() => unlockUI());
            } else {
                unlockUI();
            }
        }).catch(() => {
            unlockUI();
        });
        }).catch(() => {
            unlockUI();
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (isResettingRef.current) return;
            if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
                setSessionInitialized(true);
            }

            // V7003: JWT EXPIRY INTERCEPTOR (Previene app congelada tras meses sin abrir)
            if (event === 'SIGNED_OUT') {
                console.warn("[AuthContext] SIGNED_OUT detected. JWT likely expired. Purging session...");
                localStorage.removeItem('aura_user_data');
                localStorage.removeItem('aura_diet_status');
                localStorage.removeItem('aura_generation_type');
                localStorage.removeItem('aura_subscription_status');
                localStorage.removeItem('aura_onboarding_completed');
                localStorage.removeItem('aura_last_user_id');
                setUser(null);
                setSession(null);
                setOnboardingCompleted(false);
                setSubscriptionStatus(null);
                setLoading(false);
                
                if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
                   window.location.href = '/login';
                }
                return;
            }

            // SIMULATION OVERRIDE IN AUTH STATE CHANGE (Unlikely to fire for mock, but safe to keep default logic)
            setSession(session);
            const activeUser = session?.user ?? null;
            setUser(activeUser);

            if (activeUser) {
                const { hasCache } = hydrateStates(activeUser.id);
                localStorage.setItem('aura_last_user_id', activeUser.id);

                if (hasCache) {
                    setLoading(false);
                }

                subscribeToProfileChanges(activeUser.id);
                syncUserData(activeUser.id).finally(() => setLoading(false)); // Don't delay on subsequent auth state changes
            } else {
                if (realtimeChannelRef.current) {
                    supabase.removeChannel(realtimeChannelRef.current);
                    realtimeChannelRef.current = null;
                }
                setLoading(false);
            }
        });

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log("[AuthContext] App visible again. Verifying profile sync...");
                const storedUserId = localStorage.getItem('aura_last_user_id');
                if (storedUserId) {
                    syncUserData(storedUserId);
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (subscription) subscription.unsubscribe();
            if (realtimeChannelRef.current) {
                supabase.removeChannel(realtimeChannelRef.current);
            }
            clearTimeout(safetyTimeout);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const signOut = async () => {
        try {
            // 1. Clear core auth state
            setUser(null);
            setSession(null);
            setSubscriptionStatus(null);
            setOnboardingCompleted(false);

            // 2. Clear ONLY session-related local items if needed
            // Actually, we WANT to keep aura_user_data and aura_onboarding_completed 
            // so that re-login is smooth. We only clear the token indirectly via Supabase.
            // But we SHOULD remove the last user id if we want to ensure fresh hydration on next login?
            // No, the next login will set its own ID.
            
            // V999: CACHE CLEARANCE FOR UI TIMERS
            // We MUST clear the background wait states otherwise if a Basic user logs in on the same device
            // after a Premium user, they will inherit the spinning loaders and background countdowns.
            localStorage.removeItem('aura_diet_status');
            localStorage.removeItem('aura_target_completion_time');
            localStorage.removeItem('aura_diet_process_start');
            localStorage.removeItem('aura_generation_type');
            localStorage.removeItem('aura_pending_completion_reveal');
            // V2115: WIPE SUBSCRIPTION STATUS TO PREVENT CROSS-ACCOUNT POLLUTION
            localStorage.removeItem('aura_subscription_status');
            localStorage.removeItem('aura_user_role');
            localStorage.removeItem('aura_premium_welcome_seen');
            
            // V2200: WIPE WORKOUT AND HABIT DATA TO PREVENT CROSS-ACCOUNT POLLUTION
            localStorage.removeItem('aura_active_program');
            localStorage.removeItem('aura_history');
            localStorage.removeItem('aura_weekly_checkins');
            // Remove any dynamically generated daily habits
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('aura_habits_')) {
                    localStorage.removeItem(key);
                }
            });

            // 2.5 MANUALLY wipe Supabase tokens to prevent zombie session bugs
            // Because if signOut fails or is interrupted by navigation, the session persists
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
                    localStorage.removeItem(key);
                }
            });

            // 3. Supabase SignOut
            await supabase.auth.signOut();
        } catch (e) {
            console.error("[AuthContext] SignOut error:", e);
        }
    };

    const refreshSubscription = async () => {
        if (user) await syncUserData(user.id);
    };

    const confirmOnboardingAction = () => {
        localStorage.setItem('aura_onboarding_completed', 'true');
        setOnboardingCompleted(true);
    };

    const atomicReset = async () => {
        if (isResettingRef.current) return;
        isResettingRef.current = true;
        setLoading(true);
        const userId = user?.id;

        let preservedStats = null;
        try {
            const localData = JSON.parse(localStorage.getItem('aura_user_data') || '{}');
            if (localData.consent) {
                preservedStats = { consent: localData.consent };
            }
        } catch (e) { }

        // --- CANCEL STRIPE SUBSCRIPTION ---
        const cancelSubPromise = async () => {
            if (userId && subscriptionStatus !== 'free') {
                console.log("[AuthContext] Attempting to cancel Stripe subscription...");
                const { error: cancelError } = await supabase.functions.invoke('manage-subscription', {
                    body: { action: 'cancel_immediately' }
                });
                if (cancelError) {
                    console.warn("[AuthContext] Error canceling subscription:", cancelError);
                } else {
                    console.log("[AuthContext] Stripe subscription successfully cancelled.");
                }
            }
        };

        // --- WIPE ALL CHAT AND PROGRESS DATA ---
        const wipeDataPromise = async () => {
            if (userId) {
                console.log("[AuthContext] Delegating hard data wipe to Edge Function...");
                const { error: wipeError } = await supabase.functions.invoke('sys-sync-db', {
                    body: { target_user_id: userId }
                });
                if (wipeError) {
                    console.error("[AuthContext] Edge function aura-reset failed:", wipeError);
                    
                    // FALLBACK: AdBlockers might block Edge Functions entirely. Perform aggressive local REST clear.
                    try {
                        console.log("[AuthContext] Activating REST API fallback wipe...");
                        // Use individual deletes to avoid one failure halting the others
                        await Promise.allSettled([
                            supabase.from('chat_history').delete().eq('user_id', userId),
                            supabase.from('user_habits').delete().eq('user_id', userId),
                            supabase.from('weekly_checkins').delete().eq('user_id', userId),
                            supabase.from('workout_logs').delete().eq('user_id', userId),
                            supabase.from('user_workouts').delete().eq('user_id', userId)
                        ]);
                    } catch (e) {
                         console.error("[AuthContext] Fallback REST wipe also failed:", e);
                    }
                } else {
                    console.log("[AuthContext] Account data successfully wiped.");
                }
            }
        };

        const emergency = setTimeout(() => {
            console.warn("[AuthContext] Reset took too long. Forcing emergency local clear.");
            localStorage.clear();
            sessionStorage.clear();
            if (preservedStats && preservedStats.consent) {
                localStorage.setItem('aura_user_data', JSON.stringify({ consent: preservedStats.consent }));
            }
            window.location.replace('/#/login');
            window.location.reload();
        }, 8000); // Give edge functions ample 8s to complete

        try {
            await Promise.allSettled([
                cancelSubPromise(),
                wipeDataPromise(),
            ]);

            if (userId) {
                await supabase.from('profiles').update({
                    stats: preservedStats,
                    onboarding_completed: false,
                    subscription_status: 'free',
                    stripe_customer_id: null,
                    stripe_subscription_id: null,
                }).eq('id', userId);
            }
            await supabase.auth.signOut().catch(() => { });
            clearTimeout(emergency);
            localStorage.clear();
            if (preservedStats && preservedStats.consent) {
                localStorage.setItem('aura_user_data', JSON.stringify({ consent: preservedStats.consent }));
            }
            window.location.replace('/#/');
            window.location.reload();
        } catch (e) {
            clearTimeout(emergency);
            localStorage.clear();
            if (preservedStats && preservedStats.consent) {
                localStorage.setItem('aura_user_data', JSON.stringify({ consent: preservedStats.consent }));
            }
            window.location.replace('/#/');
            window.location.reload();
        }
    };

    const hardDeleteAccount = async () => {
        if (isResettingRef.current) return;
        isResettingRef.current = true;
        setLoading(true);
        const userId = user?.id;

        try {
            // --- CANCEL STRIPE SUBSCRIPTION ---
            if (userId && subscriptionStatus !== 'free') {
                console.log("[AuthContext] Attempting to cancel Stripe subscription...");
                try {
                    await supabase.functions.invoke('manage-subscription', {
                        body: { action: 'cancel_immediately' }
                    });
                } catch (e) {
                    console.error("[AuthContext] Failed network call to manage-subscription:", e);
                }
            }

            if (userId) {
                console.log("[AuthContext] Initiating Hard Delete via Edge Function...");
                const { error: hardDeleteError } = await supabase.functions.invoke('delete-account');
                if (hardDeleteError) {
                    console.error("[AuthContext] Hard Delete Error:", hardDeleteError);
                    throw new Error("No se pudo completar el borrado en el servidor. Inténtalo de nuevo.");
                }
                console.log("[AuthContext] Hard Delete successful on backend.");
            }

            await supabase.auth.signOut().catch(() => { });

            // --- LOCAL CLEANUP ---
            localStorage.clear();
            sessionStorage.clear();
            window.location.replace('/#/');
            window.location.reload();
        } catch (e: any) {
            console.error("[AuthContext] Exception during Hard Delete:", e);
            setLoading(false);
            isResettingRef.current = false;
            throw e; // Let the caller UI handle the error (e.g. show toast and don't log out)
        }
    };

    return (
        <AuthContext.Provider value={{
            user, session, loading, isSyncing, signOut, subscriptionStatus,
            refreshSubscription, userRole, setUserRole, onboardingCompleted, confirmOnboardingAction, atomicReset, hardDeleteAccount,
            sessionInitialized, maintenanceMode, marcAvatarUrl
        }}>
            {children}
        </AuthContext.Provider>
    );
};
